/**
 * Adding a coffee account, or changing one: a name, and the provider's own
 * sign-in fields, drawn from what the server lists — so a new provider needs
 * no code here.
 *
 * - **The server signs in before it saves**, so a wrong password is an error
 *   inside this dialog, and nothing is stored.
 * - **Editing never shows the stored credentials.** The fields start empty:
 *   left empty, the sign-in stays; filled, all of them replace it.
 * - The browser is asked not to fill these fields: they are another company's
 *   password, not this application's.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  COFFEE_LIMITS,
  type CoffeeAccountDto,
  type CoffeeCredentialFieldDto,
  type CoffeeProviderDto,
} from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { useSubmit } from '../../hooks/useSubmit';
import { createAccount, updateAccount } from './api';

/** Translations for the field names providers use. Anything else shows the provider's English label. */
const FIELD_KEYS = { email: 'field.email', password: 'field.password' } as const;

export interface AccountDialogProps {
  open: boolean;
  providers: readonly CoffeeProviderDto[];
  /** Null: add a new one. */
  account: CoffeeAccountDto | null;
  onClose: () => void;
  onSaved: () => void;
}

export function AccountDialog({ open, providers, account, onClose, onSaved }: AccountDialogProps) {
  const { t } = useTranslation('coffee');
  const [providerKey, setProviderKey] = useState('');
  const [label, setLabel] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setProviderKey(account?.provider ?? providers[0]?.key ?? '');
    setLabel(account?.label ?? '');
    setCredentials({});
  }, [open, account, providers]);

  const provider = providers.find((p) => p.key === providerKey);
  const fields = provider?.fields ?? [];
  const filled = fields.filter((field) => (credentials[field.name] ?? '') !== '').length;
  const credentialsComplete = filled === fields.length;
  // A new account needs every field; an edit needs all of them or none.
  const credentialsValid = account ? filled === 0 || credentialsComplete : credentialsComplete;

  const save = useSubmit(async () => {
    const trimmed = label.trim();
    const given = Object.fromEntries(fields.map((field) => [field.name, credentials[field.name] ?? '']));
    const result = account
      ? await updateAccount(account.id, {
          ...(trimmed !== account.label ? { label: trimmed } : {}),
          ...(filled > 0 ? { credentials: given } : {}),
        })
      : await createAccount({ provider: providerKey, label: trimmed, credentials: given });
    onSaved();
    return result;
  });

  const fieldLabel = (field: CoffeeCredentialFieldDto) =>
    field.name in FIELD_KEYS ? t(FIELD_KEYS[field.name as keyof typeof FIELD_KEYS]) : field.label;

  const unchanged = account !== null && label.trim() === account.label && filled === 0;

  return (
    <FormDialog
      open={open}
      title={account ? t('dialog.editTitle', { label: account.label }) : t('dialog.createTitle')}
      submitLabel={t('dialog.save')}
      pending={save.pending}
      error={save.error}
      disabled={!provider || label.trim() === '' || !credentialsValid || unchanged}
      onSubmit={() => void save.submit()}
      onClose={() => {
        save.clearError();
        onClose();
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          select
          label={t('dialog.provider')}
          value={providerKey}
          disabled={account !== null || providers.length < 2}
          onChange={(event) => {
            setProviderKey(event.target.value);
            setCredentials({});
          }}
        >
          {providers.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('dialog.label')}
          helperText={t('dialog.labelHelp')}
          value={label}
          required
          slotProps={{ htmlInput: { maxLength: COFFEE_LIMITS.labelLength } }}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Typography variant="body2" color="text.secondary">
          {account ? t('dialog.keepCredentials') : t('dialog.credentials')}
        </Typography>
        {fields.map((field) => (
          <TextField
            key={field.name}
            label={fieldLabel(field)}
            type={field.kind === 'password' ? 'password' : field.kind === 'email' ? 'email' : 'text'}
            autoComplete={field.kind === 'password' ? 'new-password' : 'off'}
            required={!account}
            value={credentials[field.name] ?? ''}
            slotProps={{ htmlInput: { maxLength: COFFEE_LIMITS.credentialLength } }}
            onChange={(event) => setCredentials((current) => ({ ...current, [field.name]: event.target.value }))}
          />
        ))}
      </Stack>
    </FormDialog>
  );
}
